import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { DRAFT_SCOPES } from '../../utils/draftKeys';
import ProeprtyCreate from '../../User/Pages/Components/froms/ProeprtyCreate';
import ProjectCreate from '../../User/Pages/Components/froms/ProjectCreate';
import './AdminPages.css';
import AppIcon from '../../Components/AppIcon';

// ─── Listing Type Modal ────────────────────────────────────────────────────────
const ListingTypeModal = ({ onSelect, onCancel }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <div
      style={{
        background: '#fff', borderRadius: '1.25rem',
        padding: '2.5rem 2rem', width: '100%', maxWidth: 480,
        boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
        animation: 'fadeSlideUp 0.22s ease',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{
          width: 56, height: 56, background: '#eff6ff', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1rem',
        }}>
          <span style={{ fontSize: 26, display: 'inline-flex' }}><AppIcon name="home" size={26} /></span>
        </div>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
          Select Listing Type
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '0.4rem' }}>
          This is required before creating a property
        </p>
      </div>

      {/* Options */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <button
          onClick={() => onSelect('Sale')}
          style={{
            padding: '1.5rem 1rem',
            border: '2px solid #e2e8f0',
            borderRadius: '0.875rem',
            background: '#f8fafc',
            cursor: 'pointer',
            transition: 'all 0.18s',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#3b82f6';
            e.currentTarget.style.background = '#eff6ff';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(59,130,246,0.15)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.background = '#f8fafc';
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ fontSize: 36, display: 'inline-flex' }}><AppIcon name="tag" size={36} /></span>
          <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>For Sale</span>
          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Property listed for purchase</span>
        </button>

        <button
          onClick={() => onSelect('Rent')}
          style={{
            padding: '1.5rem 1rem',
            border: '2px solid #e2e8f0',
            borderRadius: '0.875rem',
            background: '#f8fafc',
            cursor: 'pointer',
            transition: 'all 0.18s',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#8b5cf6';
            e.currentTarget.style.background = '#f5f3ff';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(139,92,246,0.15)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.background = '#f8fafc';
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ fontSize: 36, display: 'inline-flex' }}><AppIcon name="key" size={36} /></span>
          <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>For Rent</span>
          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Property listed for rental</span>
        </button>
      </div>

      {/* Cancel */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <button
          onClick={onCancel}
          style={{
            background: 'none', border: 'none', color: '#94a3b8',
            fontSize: '0.875rem', cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          Cancel — go back
        </button>
      </div>
    </div>

    {/* Animation keyframes injected inline */}
    <style>{`
      @keyframes fadeSlideUp {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  </div>
);

// ─── CreateListingWrapper ─────────────────────────────────────────────────────
const CreateListingWrapper = () => {
  const { pathname } = useLocation();
  const draftScope = pathname.startsWith('/partner') ? DRAFT_SCOPES.PARTNER : DRAFT_SCOPES.ADMIN;
  const [searchParams] = useSearchParams();
  const editId     = searchParams.get('editId');
  const typeParam  = searchParams.get('type');   // 'property' | 'project'

  // Step 1: choose property vs project
  const [listingType, setListingType] = useState(typeParam || null);

  // Step 2 (property only): choose Rent vs Sale via modal
  const [showListingTypeModal, setShowListingTypeModal] = useState(false);
  const [selectedListingType, setSelectedListingType]   = useState(null);  // 'Sale' | 'Rent'

  useEffect(() => {
    if (typeParam) setListingType(typeParam);
  }, [typeParam]);

  // When admin clicks "Create Property", show modal first (only on create, not edit)
  const handlePropertyClick = () => {
    if (editId) {
      // Edit mode: skip modal — listing_type is already stored in the property
      setListingType('property');
    } else {
      setShowListingTypeModal(true);
    }
  };

  const handleListingTypeSelect = (type) => {
    setSelectedListingType(type);
    setShowListingTypeModal(false);
    setListingType('property');
  };

  const handleListingTypeCancel = () => {
    setShowListingTypeModal(false);
  };

  // Step 1 — type selection screen
  if (!listingType && !showListingTypeModal) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>Create New Listing</h1>
          <p>Select what type of listing you want to create</p>
        </div>
        <div className="admin-card">
          <div className="filter-box-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <button
              className="btn-primary"
              style={{ padding: '2rem', fontSize: '1.25rem' }}
              onClick={handlePropertyClick}
            >
              <AppIcon name="home" size={20} /> Create Property
            </button>
            <button
              className="btn-secondary"
              style={{ padding: '2rem', fontSize: '1.25rem', justifyContent: 'center' }}
              onClick={() => setListingType('project')}
            >
              <AppIcon name="project" size={20} /> Create Project
            </button>
          </div>
        </div>

        {/* Listing Type Modal — shown on top of the selection screen */}
        {showListingTypeModal && (
          <ListingTypeModal
            onSelect={handleListingTypeSelect}
            onCancel={handleListingTypeCancel}
          />
        )}
      </div>
    );
  }

  // Modal shown before the form loads
  if (showListingTypeModal) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>Create New Listing</h1>
        </div>
        <ListingTypeModal
          onSelect={handleListingTypeSelect}
          onCancel={handleListingTypeCancel}
        />
      </div>
    );
  }

  // Step 2 — form with pre-selected listing type
  const isEditMode = !!editId;
  return (
    <div className="admin-page">
      <div className="page-header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>{isEditMode ? 'Edit' : 'Create'} {listingType === 'property' ? 'Property' : 'Project'}</h1>
          {listingType === 'property' && selectedListingType && (
            <div style={{ marginTop: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                background: selectedListingType === 'Sale' ? '#eff6ff' : '#f5f3ff',
                color: selectedListingType === 'Sale' ? '#1d4ed8' : '#7c3aed',
                border: `1.5px solid ${selectedListingType === 'Sale' ? '#bfdbfe' : '#ddd6fe'}`,
                borderRadius: '1rem', padding: '0.2rem 0.75rem', fontSize: '0.8rem', fontWeight: 700,
              }}>
                <AppIcon name={selectedListingType === 'Sale' ? 'tag' : 'key'} size={16} /> For {selectedListingType}
              </span>
              {!isEditMode && (
                <button
                  type="button"
                  onClick={() => { setShowListingTypeModal(true); setListingType(null); setSelectedListingType(null); }}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Change
                </button>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            if (isEditMode) {
              window.history.back();
              return;
            }
            setListingType(null);
            setSelectedListingType(null);
          }}
        >
          ← Back
        </button>
      </div>
      <div className="admin-card user-form-wrapper">
        {listingType === 'property'
          ? <ProeprtyCreate listingTypeProp={selectedListingType} draftScope={draftScope} enableOwnerNamePanel />
          : <ProjectCreate draftScope={draftScope} enableOwnerNamePanel />
        }
      </div>
    </div>
  );
};

export default CreateListingWrapper;
